"use client";

import { Download, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const playbookItems = [{
  index: "01",
  title: "Material Selection Guide",
  steps: ["Assess environmental exposure (UV, chemical, heat).", "Choose between phenolic, acrylic, or stainless steel.", "Verify thickness requirements for mounting surfaces.", "Ensure material meets UL 508A compliance standards.", "Consult our technical team for custom material specs."]
}, {
  index: "02",
  title: "Submittal Process",
  steps: ["Upload your project drawings or equipment schedules.", "Our team reviews specifications for accuracy.", "Receive a digital proof for final sign-off.", "Approve the layout to initiate production.", "Track your order status through our portal."]
}, {
  index: "03",
  title: "Installation Best Practices",
  steps: ["Clean mounting surfaces thoroughly before application.", "Use recommended industrial-grade adhesives.", "Ensure mechanical fasteners are torqued correctly.", "Verify alignment with electrical panel schematics.", "Inspect labels for proper adhesion after 24 hours."]
}, {
  index: "04",
  title: "Arc Flash Compliance",
  steps: ["Identify hazard levels based on NFPA 70E standards.", "Ensure labels include required warning headers.", "Verify all data points match current arc flash studies.", "Place labels in highly visible, accessible locations.", "Update labels immediately following equipment changes."]
}, {
  index: "05",
  title: "Direct Part Marking (DPM)",
  steps: ["Select the appropriate laser marking technique.", "Ensure surface finish is compatible with laser etching.", "Verify barcode readability for inventory tracking.", "Maintain consistent depth for long-term durability.", "Perform quality checks on every batch produced."]
}, {
  index: "06",
  title: "Custom Engraving Specs",
  steps: ["Define font size and style for maximum legibility.", "Specify color contrast for high-visibility areas.", "Provide vector files for precise logo reproduction.", "Confirm hole placement for mounting hardware.", "Review final dimensions against site requirements."]
}, {
  index: "07",
  title: "Quality Assurance",
  steps: ["Every piece undergoes a multi-point inspection.", "Verify spelling and technical data against submittals.", "Check material integrity and edge finish quality.", "Ensure protective films are intact for shipping.", "Package securely to prevent damage during transit."]
}];
export default function FaqAccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1275" id="faq-accordion" className="bg-background text-foreground relative py-32 md:py-44 overflow-hidden" style={{
    clipPath: "polygon(0 7%, 100% 0, 100% 100%, 0 100%)"
  }}>
      <div ref={ref} className={`container mx-auto px-6 md:px-12 max-w-[1400px] transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="block w-6 h-px bg-primary"></span>
                <span className="font-mono text-[0.75rem] tracking-[0.18em] uppercase text-muted-foreground">
                  Technical Resources
                </span>
              </div>

              {}
              <h2 className="font-default uppercase text-foreground text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.92] tracking-[-0.005em] mb-8">
                The{" "}
                <em className="not-italic">
                  <span className="italic text-primary">GUIDE</span>
                </em>
                .
              </h2>

              {}
              <p className="font-default text-[clamp(1.0625rem,1.2vw,1.25rem)] leading-[1.6] text-foreground/70 max-w-md">
                Expert protocols for material selection, submittal workflows, and industrial labeling standards.
              </p>

              {}
              <div className="hidden lg:block mt-12 font-mono text-[0.6875rem] tracking-[0.15em] uppercase text-muted-foreground">
                Precision Engraving / Industrial Solutions
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {playbookItems.map((item, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group">
                  <AccordionTrigger className="hover:no-underline py-0 [&>svg]:hidden">
                    <div className="flex items-center justify-between w-full min-h-[96px] py-6 gap-6 group-hover:pl-2 transition-all duration-200">
                      <div className="flex items-center gap-6 md:gap-10 flex-1">
                        <span className="font-mono text-sm md:text-base text-primary tracking-[0.1em] shrink-0">
                          {item.index} 
                        </span>
                        <h3 className="font-default uppercase text-foreground text-[clamp(1.25rem,2.4vw,2.25rem)] leading-[1] tracking-[0] text-left transition-transform duration-200 group-hover:translate-x-2">
                          {item.title}
                        </h3>
                      </div>
                      <span className="shrink-0 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45">
                        <Plus className="size-7 md:size-9" strokeWidth={1.75} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pl-0 md:pl-[5.5rem]">
                    <ol className="space-y-3 mb-8 max-w-2xl">
                      {item.steps.map((step, sIdx) => <li key={sIdx} data-index={sIdx} className="flex gap-4 font-default text-base leading-[1.65] text-foreground/80">
                          <span className="font-mono text-sm text-primary shrink-0 pt-1 min-w-[2rem]">
                            {String(sIdx + 1).padStart(2, "0")}.
                          </span>
                          <span>{step}</span>
                        </li>)}
                    </ol>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}